import React from 'react';

// ───────────────────────────────────────────────────────────────────────────
// BandgapDefectSchematic — small inline-SVG band diagram shared by the
// PL/TRPL simulator (PLPanel) and the PL Analysis tab (PLAnalysisPanel).
//
// Draws the absorber bandgap as two horizontal lines (Ec top, Ev bottom,
// labeled, with the Eg value) and each active defect as a short horizontal
// level at its energy position (Et measured from its reference band).
// Faint zone shading indicates shallow (near either band edge) vs deep
// (midgap) character. Theme-aware via the shared themeColors object `c`.
//
// Props:
//   Eg      — absorber bandgap in eV (required; renders nothing without it)
//   defects — [{ name, Et_eV, reference }] where reference is
//             'from_Ev' | 'from_Ec' (also accepts 'Ev'/'Ec' shorthands)
//   c       — themeColors(theme) palette object
//   title   — optional heading (default 'Bandgap & defect levels')
// ───────────────────────────────────────────────────────────────────────────

const SHALLOW_FRAC = 0.15; // within 15% of Eg from a band edge = shallow
const DEEP_LO = 0.35;      // middle 30% of the gap = deep (midgap)
const DEEP_HI = 0.65;

function refIsEc(reference) {
  return String(reference || '').toLowerCase().includes('ec');
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

export default function BandgapDefectSchematic({
  Eg, defects = [], c, title = 'Bandgap & defect levels',
}) {
  if (!Number.isFinite(Eg) || Eg <= 0) return null;

  // Geometry (viewBox units — SVG scales to container width)
  const W = 560;
  const H = 210;
  const bandX1 = 64;
  const bandX2 = W - 96;
  const yEc = 28;
  const yEv = H - 34;
  const gapH = yEv - yEc;
  // Energy above Ev (eV) → y coordinate
  const yOf = (E) => yEv - (Math.min(Math.max(E, 0), Eg) / Eg) * gapH;

  // Normalize defects: energy measured from Ev, clamped into the gap
  const levels = (defects || [])
    .map((d, i) => {
      const Et = Number(d.Et_eV);
      if (!Number.isFinite(Et)) return null;
      const E_fromEv = refIsEc(d.reference) ? Eg - Et : Et;
      if (E_fromEv < 0 || E_fromEv > Eg) return null;   // outside the gap
      const distEdge = Math.min(E_fromEv, Eg - E_fromEv);
      return {
        key: d.id || `${d.name || 'defect'}-${i}`,
        name: d.name || `defect ${i + 1}`,
        E_fromEv,
        shallow: distEdge <= SHALLOW_FRAC * Eg,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.E_fromEv - a.E_fromEv);

  // Horizontal columns so nearby levels (and their labels) don't overlap
  const nCols = 3;
  const segLen = 46;
  const colStep = (bandX2 - bandX1 - segLen - 120) / Math.max(nCols - 1, 1);

  const zoneLabelX = bandX2 + 8;
  const fs = 11;

  return (
    <div style={{
      marginTop: 10, padding: '10px 12px', borderRadius: 6,
      background: c.bgSecondary, border: `1px solid ${c.border}`,
      fontFamily: 'IBM Plex Mono, monospace', flex: '0 0 auto',
    }}>
      <div style={{
        fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase',
        color: c.textMuted, marginBottom: 6,
      }}>
        {title}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img"
        aria-label={`Band diagram: Eg = ${Eg.toFixed(2)} eV with ${levels.length} defect level(s)`}
        style={{ width: '100%', height: 'auto', display: 'block' }}>

        {/* Shallow zones (near Ec / near Ev) — faint green shading */}
        <rect x={bandX1} y={yEc} width={bandX2 - bandX1}
          height={SHALLOW_FRAC * gapH} fill={c.green} opacity={0.07} />
        <rect x={bandX1} y={yEv - SHALLOW_FRAC * gapH} width={bandX2 - bandX1}
          height={SHALLOW_FRAC * gapH} fill={c.green} opacity={0.07} />
        {/* Deep (midgap) zone — faint amber shading */}
        <rect x={bandX1} y={yOf(DEEP_HI * Eg)} width={bandX2 - bandX1}
          height={(DEEP_HI - DEEP_LO) * gapH} fill={c.amber} opacity={0.08} />
        {/* Zone tags (right margin) */}
        <text x={zoneLabelX} y={yEc + SHALLOW_FRAC * gapH * 0.5 + 3}
          fill={c.textMuted} fontSize={9} fontStyle="italic">shallow</text>
        <text x={zoneLabelX} y={(yEc + yEv) / 2 + 3}
          fill={c.textMuted} fontSize={9} fontStyle="italic">deep</text>
        <text x={zoneLabelX} y={yEv - SHALLOW_FRAC * gapH * 0.5 + 3}
          fill={c.textMuted} fontSize={9} fontStyle="italic">shallow</text>

        {/* Band edges */}
        <line x1={bandX1} y1={yEc} x2={bandX2} y2={yEc}
          stroke={c.text} strokeWidth={2} />
        <line x1={bandX1} y1={yEv} x2={bandX2} y2={yEv}
          stroke={c.text} strokeWidth={2} />
        <text x={bandX1 - 8} y={yEc + 4} fill={c.text} fontSize={fs}
          textAnchor="end" fontWeight="600">E<tspan baselineShift="sub" fontSize={8}>c</tspan></text>
        <text x={bandX1 - 8} y={yEv + 4} fill={c.text} fontSize={fs}
          textAnchor="end" fontWeight="600">E<tspan baselineShift="sub" fontSize={8}>v</tspan></text>

        {/* Eg arrow + value (left side) */}
        <line x1={bandX1 + 14} y1={yEc + 3} x2={bandX1 + 14} y2={yEv - 3}
          stroke={c.textMuted} strokeWidth={1} strokeDasharray="3 3" />
        <text x={bandX1 + 20} y={(yEc + yEv) / 2 - 4} fill={c.accent}
          fontSize={fs} fontWeight="600">
          E<tspan baselineShift="sub" fontSize={8}>g</tspan> = {Eg.toFixed(2)} eV
        </text>

        {/* Defect levels */}
        {levels.length === 0 && (
          <text x={(bandX1 + bandX2) / 2} y={(yEc + yEv) / 2 + 18}
            fill={c.textMuted} fontSize={10} textAnchor="middle"
            fontStyle="italic">
            no active defect levels
          </text>
        )}
        {levels.map((lv, i) => {
          const y = yOf(lv.E_fromEv);
          const x1 = bandX1 + 60 + (i % nCols) * colStep;
          const x2 = x1 + segLen;
          const color = lv.shallow ? c.green : c.amber;
          const full = `${lv.name} — Et,v = ${lv.E_fromEv.toFixed(2)} eV `
            + `(${lv.shallow ? 'shallow' : 'deep'})`;
          return (
            <g key={lv.key}>
              <title>{full}</title>
              <line x1={x1} y1={y} x2={x2} y2={y}
                stroke={color} strokeWidth={2.5} />
              <text x={x2 + 5} y={y + 3} fill={color} fontSize={10}>
                {truncate(lv.name, 20)}
              </text>
            </g>
          );
        })}
      </svg>
      <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4, lineHeight: 1.4 }}>
        Levels drawn at Et from their reference band; green = shallow
        (within {Math.round(SHALLOW_FRAC * 100)}% of a band edge),
        amber = deep. Hover a level for details.
      </div>
    </div>
  );
}
